import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart3, Eye, TrendingUp, Globe, Monitor, RefreshCw, Search,
  Link, Users, Smartphone, Laptop, Tablet, ChartBar, Download,
  ArrowUpRight, ArrowDownRight, Minus, Zap, MousePointerClick,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, BarChart, Bar, Legend, PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import LoaderService from "../services/LoaderService";
import {
  GetSearchConsoleOverview, GetSearchConsoleKeywords,
  GetSearchConsoleCountries, GetSearchConsoleDevices,
  GetAnalyticsDailyUsers, GetAnalyticsTrafficSources,
  GetAnalyticsTopPages, GetAnalyticsEvents,
  GetAnalyticsBrowsers, GetAnalyticsOS, GetAnalyticsLandingPages,
} from "./apis/get";

// ── constants ─────────────────────────────────────────────────────────────────

const PERIOD_OPTIONS = [
  { value: "7d",  label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "3m",  label: "Last 3 Months" },
  { value: "6m",  label: "Last 6 Months" },
  { value: "12m", label: "Last 12 Months" },
  { value: "all", label: "All Time" },
] as const;

type Period = typeof PERIOD_OPTIONS[number]["value"];

const CHART_COLORS = ["#06b6d4","#8b5cf6","#10b981","#f59e0b","#ef4444","#ec4899","#3b82f6","#14b8a6","#f97316","#a855f7"];

const TT = {
  contentStyle: { backgroundColor: "#0f172a", borderColor: "#1e293b", borderRadius: "12px" },
  labelStyle:   { color: "#94a3b8", fontSize: "12px" },
  itemStyle:    { color: "#e2e8f0", fontSize: "12px" },
};

// ── formatters ────────────────────────────────────────────────────────────────

const fmt = (v: unknown) => {
  const n = Number(v);
  return Number.isNaN(n) ? (v == null || v === "" ? "—" : String(v))
    : new Intl.NumberFormat("en-IN").format(n);
};

const fmtPct = (v: unknown) => {
  const n = Number(v);
  return Number.isNaN(n) ? "—" : `${n.toFixed(1)}%`;
};

const fmtDuration = (seconds: unknown) => {
  const s = Number(seconds);
  if (Number.isNaN(s) || s === 0) return "—";
  if (s < 60) return `${s.toFixed(0)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};

const fmtKPI = (key: string, v: unknown) => {
  if (key === "engagement_rate" || key === "bounce_rate") {
    const n = Number(v);
    return Number.isNaN(n) ? "—" : `${(n * 100).toFixed(1)}%`;
  }
  if (key === "avg_session_duration" || key === "avg_engagement_time") return fmtDuration(v);
  return fmt(v);
};

const normalizePayload = (p: any) =>
  p && typeof p === "object" && "data" in p && p.data !== undefined ? p.data : p;

// ── reusable components ───────────────────────────────────────────────────────

const Trend = ({ pct }: { pct: number | null | undefined }) => {
  if (pct == null) return null;
  if (pct > 0) return (
    <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-400">
      <ArrowUpRight size={12} />{pct.toFixed(1)}%
    </span>
  );
  if (pct < 0) return (
    <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-rose-400">
      <ArrowDownRight size={12} />{Math.abs(pct).toFixed(1)}%
    </span>
  );
  return <span className="inline-flex items-center gap-0.5 text-xs text-slate-500"><Minus size={10} />0%</span>;
};

const KPICard = ({ title, value, icon: Icon, color, border, trend }: {
  title: string; value: string; icon: any; color: string; border: string; trend?: number | null;
}) => (
  <div className={`rounded-2xl border ${border} bg-slate-900/80 p-5 shadow-lg hover:shadow-xl transition-shadow duration-300`}>
    <div className="flex items-start justify-between">
      <div className="space-y-1">
        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{title}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
        {trend !== undefined && <Trend pct={trend} />}
      </div>
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}>
        <Icon size={18} />
      </span>
    </div>
  </div>
);

const SecondaryMetric = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
    <p className="text-xs text-slate-400 mb-0.5">{label}</p>
    <p className="text-lg font-semibold text-slate-100">{value}</p>
  </div>
);

const SectionCard = ({ title, icon: Icon, children, error, subtitle, badge }: {
  title: string; icon: any; children: React.ReactNode;
  error?: string; subtitle?: string; badge?: string;
}) => (
  <section className="rounded-2xl border border-slate-800 bg-slate-900/70 shadow-lg shadow-black/20">
    <header className="flex items-center justify-between gap-3 border-b border-slate-800 px-4 py-3 sm:px-5">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-300">
          <Icon size={16} />
        </span>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
            {badge && (
              <span className="rounded-full bg-cyan-500/10 border border-cyan-500/20 px-2 py-0.5 text-[10px] font-semibold text-cyan-300 uppercase tracking-wider">
                {badge}
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {error && (
        <span className="rounded-full border border-rose-500/40 bg-rose-500/10 px-3 py-1 text-xs font-medium text-rose-300">
          {error}
        </span>
      )}
    </header>
    <div className="p-4 sm:p-5">{children}</div>
  </section>
);

const SectionDivider = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 pt-2">
    <div className="h-px flex-1 bg-slate-800" />
    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
    <div className="h-px flex-1 bg-slate-800" />
  </div>
);

const EmptyState = ({ loading, label }: { loading: boolean; label: string }) => (
  <div className="py-10 text-center text-slate-500 text-sm">
    {loading ? "Loading…" : `No ${label} data available for this period.`}
  </div>
);

const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
  if (percent < 0.05) return null;
  const R = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  return (
    <text x={cx + r * Math.cos(-midAngle * R)} y={cy + r * Math.sin(-midAngle * R)}
      fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const HorizBar = ({ data, dataKey, color = "#06b6d4", nameKey = "name", height = 260 }: {
  data: any[]; dataKey: string; color?: string; nameKey?: string; height?: number;
}) => (
  <div style={{ height }}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
        <XAxis type="number" stroke="#475569" fontSize={10} tickLine={false} axisLine={false} />
        <YAxis type="category" dataKey={nameKey} stroke="#475569" fontSize={10} tickLine={false} width={100} />
        <Tooltip {...TT} />
        <Bar dataKey={dataKey} fill={color} radius={[0, 4, 4, 0]}>
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const DonutChart = ({ data, height = 220 }: { data: { name: string; value: number }[]; height?: number }) => (
  <div style={{ height }}>
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={90}
          paddingAngle={3} dataKey="value" labelLine={false} label={renderPieLabel}>
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <Tooltip contentStyle={TT.contentStyle} formatter={(v: any, n: any) => [fmt(v), n]} />
      </PieChart>
    </ResponsiveContainer>
  </div>
);

const PieLegend = ({ data }: { data: { name: string; value: number }[] }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="flex flex-wrap justify-center gap-2 mt-1">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900/60 px-2.5 py-1.5">
          <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
          <span className="text-xs font-medium text-slate-200 capitalize">{d.name}</span>
          <span className="text-xs text-slate-500">{total > 0 ? ((d.value / total) * 100).toFixed(1) : 0}%</span>
        </div>
      ))}
    </div>
  );
};

const ScrollTable = ({ headers, rows, emptyMsg }: {
  headers: string[]; rows: React.ReactNode[][]; emptyMsg: string;
}) => (
  <div className="overflow-auto rounded-xl border border-slate-800 max-h-64">
    <table className="min-w-full divide-y divide-slate-800 text-sm">
      <thead className="bg-slate-950/70 text-slate-400 sticky top-0">
        <tr>
          {headers.map((h, i) => (
            <th key={i} className={`px-3 py-2 font-medium ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-800 bg-slate-900/40">
        {rows.length > 0 ? rows.map((cells, ri) => (
          <tr key={ri} className="hover:bg-slate-900/80">
            {cells.map((cell, ci) => (
              <td key={ci} className={`px-3 py-2 ${ci === 0 ? "font-medium text-slate-100" : "text-right text-slate-300"}`}>
                {cell}
              </td>
            ))}
          </tr>
        )) : (
          <tr><td colSpan={headers.length} className="px-3 py-6 text-center text-slate-500">{emptyMsg}</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

// ── main component ────────────────────────────────────────────────────────────

export default function GoogleOverview() {
  const [period, setPeriod] = useState<Period>("30d");

  // GA4 state
  const [overview,       setOverview]       = useState<any>({});
  const [dailyUsers,     setDailyUsers]     = useState<any[]>([]);
  const [topPages,       setTopPages]       = useState<any[]>([]);
  const [landingPages,   setLandingPages]   = useState<any[]>([]);
  const [trafficSources, setTrafficSources] = useState<any[]>([]);
  const [devices,        setDevices]        = useState<any[]>([]);
  const [countries,      setCountries]      = useState<any[]>([]);
  const [events,         setEvents]         = useState<any[]>([]);
  const [browsers,       setBrowsers]       = useState<any[]>([]);
  const [os,             setOs]             = useState<any[]>([]);

  // Search Console state
  const [scKeywords, setScKeywords] = useState<any[]>([]);

  const [loading,     setLoading]     = useState(true);
  const [errors,      setErrors]      = useState<Record<string, string>>({});
  const [lastUpdated, setLastUpdated] = useState<string>("");

  const loadAnalytics = async (p: Period = period) => {
    setLoading(true);
    LoaderService.show();

    const requests: [string, () => Promise<any>][] = [
      ["overview",       () => GetSearchConsoleOverview(p)],
      ["dailyUsers",     () => GetAnalyticsDailyUsers(p)],
      ["topPages",       () => GetAnalyticsTopPages(p)],
      ["landingPages",   () => GetAnalyticsLandingPages(p)],
      ["trafficSources", () => GetAnalyticsTrafficSources(p)],
      ["devices",        () => GetSearchConsoleDevices(p)],
      ["countries",      () => GetSearchConsoleCountries(p)],
      ["events",         () => GetAnalyticsEvents(p)],
      ["browsers",       () => GetAnalyticsBrowsers(p)],
      ["os",             () => GetAnalyticsOS(p)],
      ["scKeywords",     () => GetSearchConsoleKeywords(p)],
    ];

    try {
      const settled = await Promise.allSettled(requests.map(([, fn]) => fn()));
      const nextErrors: Record<string, string> = {};

      settled.forEach((result, i) => {
        const [key] = requests[i];
        if (result.status === "fulfilled") {
          const d = normalizePayload(result.value);
          if (key === "overview")       setOverview(d || {});
          if (key === "dailyUsers")     setDailyUsers(Array.isArray(d) ? d : []);
          if (key === "topPages")       setTopPages(Array.isArray(d) ? d : []);
          if (key === "landingPages")   setLandingPages(Array.isArray(d) ? d : []);
          if (key === "trafficSources") setTrafficSources(Array.isArray(d) ? d : []);
          if (key === "devices")        setDevices(Array.isArray(d) ? d : []);
          if (key === "countries")      setCountries(Array.isArray(d) ? d : []);
          if (key === "events")         setEvents(Array.isArray(d) ? d : []);
          if (key === "browsers")       setBrowsers(Array.isArray(d) ? d : []);
          if (key === "os")             setOs(Array.isArray(d) ? d : []);
          if (key === "scKeywords")     setScKeywords(Array.isArray(d) ? d : []);
        } else {
          nextErrors[key] = result.reason?.message || "Failed to load";
        }
      });

      setErrors(nextErrors);
      setLastUpdated(new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }));
    } finally {
      setLoading(false);
      LoaderService.hide();
    }
  };

  useEffect(() => { loadAnalytics(period); }, [period]); // eslint-disable-line

  // ── derived data ────────────────────────────────────────────────────────────

  const dailyChartData = useMemo(() =>
    [...dailyUsers]
      .sort((a, b) => String(a.date).localeCompare(String(b.date)))
      .map((item) => {
        const s = String(item.date);
        let label = s;
        if (/^\d{8}$/.test(s)) {
          const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
          const mi = parseInt(s.slice(4, 6), 10) - 1;
          label = `${parseInt(s.slice(6, 8), 10)} ${MONTHS[mi] ?? ""}`;
        }
        return { label, Users: Number(item.users || 0), Sessions: Number(item.sessions || 0), Pageviews: Number(item.pageviews || 0) };
      }),
  [dailyUsers]);

  const devicePieData  = useMemo(() => devices.map(d => ({ name: d.device || "-", value: Number(d.users || 0) })), [devices]);
  const browserPieData = useMemo(() => browsers.map(d => ({ name: d.browser || "-", value: Number(d.users || 0) })), [browsers]);
  const osPieData      = useMemo(() => os.map(d => ({ name: d.os || "-", value: Number(d.users || 0) })), [os]);

  const channelBarData = useMemo(() =>
    trafficSources
      .reduce((acc: any[], d) => {
        const ch = d.channel || `${d.source}/${d.medium}`;
        const ex = acc.find(x => x.name === ch);
        if (ex) { ex.Sessions += Number(d.sessions || 0); ex.Users += Number(d.users || 0); }
        else acc.push({ name: ch, Sessions: Number(d.sessions || 0), Users: Number(d.users || 0) });
        return acc;
      }, [])
      .sort((a, b) => b.Sessions - a.Sessions)
      .slice(0, 8),
  [trafficSources]);

  const countriesBarData = useMemo(() =>
    countries.slice(0, 10).map(d => ({ name: d.country || "-", Users: Number(d.users || 0) })),
  [countries]);

  const topPagesBarData = useMemo(() =>
    topPages.slice(0, 10).map(d => ({
      name: (d.page || "/").replace(/^https?:\/\/[^/]+/, "").slice(0, 30) || "/",
      Views: Number(d.views || 0),
      Users: Number(d.users || 0),
    })),
  [topPages]);

  const eventsBarData = useMemo(() =>
    events.slice(0, 12).map(d => ({ name: d.event || "-", Count: Number(d.count || 0) })),
  [events]);

  const failedSections = useMemo(() => {
    const map: Record<string, string> = {
      overview: "Overview", dailyUsers: "Daily Users", topPages: "Top Pages",
      landingPages: "Landing Pages", trafficSources: "Traffic Sources",
      devices: "Devices", countries: "Countries", events: "Events",
      browsers: "Browsers", os: "Operating Systems", scKeywords: "Search Console Keywords",
    };
    return Object.entries(errors).map(([k, m]) => `${map[k] || k}: ${m}`);
  }, [errors]);

  const cmp = overview.comparison || {};

  // ── Excel export (backend) ─────────────────────────────────────────────────

  const [exporting, setExporting] = useState(false);

  const downloadExcel = async () => {
    setExporting(true);
    try {
      const host = (localStorage.getItem("apiHost") || "http://localhost:8008/").replace(/\/$/, "");
      const token = localStorage.getItem("token") || "";
      const res = await fetch(`${host}/analytics/export?period=${period}`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] ?? `Smart_Matrimony_Analytics_${period}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">

        {/* ── HEADER ── */}
        <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-950/30 p-5 shadow-2xl sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">
                <ChartBar size={13} /> GA4 + Search Console
              </div>
              <h1 className="text-2xl font-bold text-white sm:text-3xl">Analytics Platform</h1>
              <p className="text-sm text-slate-400">Smart Matrimony · Real-time GA4 data</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="rounded-xl border border-slate-800 bg-slate-900/80 px-3 py-2 text-xs">
                <div className="text-slate-500 uppercase tracking-wider text-[10px]">Refreshed</div>
                <div className="font-medium text-slate-200 mt-0.5">{lastUpdated || "—"}</div>
              </div>
              <select value={period} onChange={(e) => setPeriod(e.target.value as Period)} disabled={loading}
                className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm font-medium text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-60">
                {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button onClick={downloadExcel} disabled={loading || exporting || !lastUpdated}
                className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-60">
                <Download size={14} /> {exporting ? "Exporting…" : "Export Excel"}
              </button>
              <button onClick={() => loadAnalytics(period)} disabled={loading}
                className="inline-flex items-center gap-1.5 rounded-xl bg-cyan-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-cyan-400 disabled:opacity-60">
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
            </div>
          </div>
          {failedSections.length > 0 && (
            <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs text-amber-200">
              <span className="font-semibold">Failed: </span>{failedSections.join(" · ")}
            </div>
          )}
        </div>

        {/* ── OVERVIEW ── */}
        <SectionDivider label="Overview" />

        {/* Primary KPIs */}
        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { key: "active_users",  label: "Active Users",    icon: Users,      color: "bg-blue-500/10 text-blue-400",   border: "border-blue-500/20",   trend: cmp.active_users },
            { key: "sessions",      label: "Sessions",        icon: RefreshCw,  color: "bg-purple-500/10 text-purple-400", border: "border-purple-500/20", trend: cmp.sessions },
            { key: "page_views",    label: "Page Views",      icon: Eye,        color: "bg-amber-500/10 text-amber-400",  border: "border-amber-500/20",  trend: cmp.page_views },
            { key: "new_users",     label: "New Users",       icon: ArrowUpRight, color: "bg-emerald-500/10 text-emerald-400", border: "border-emerald-500/20", trend: cmp.new_users },
          ].map(({ key, label, icon, color, border, trend }) => (
            <KPICard key={key} title={label} icon={icon} color={color} border={border}
              value={loading ? "—" : fmtKPI(key, overview[key])}
              trend={overview.has_comparison ? trend : undefined} />
          ))}
        </section>

        {/* Secondary metrics */}
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SecondaryMetric label="Engagement Rate"
            value={loading ? "—" : fmtPct(overview.engagement_rate ? overview.engagement_rate * 100 : 0)} />
          <SecondaryMetric label="Bounce Rate"
            value={loading ? "—" : fmtPct(overview.bounce_rate ? overview.bounce_rate * 100 : 0)} />
          <SecondaryMetric label="Avg Session Duration"
            value={loading ? "—" : fmtDuration(overview.avg_session_duration)} />
          <SecondaryMetric label="Avg Engagement Time / Session"
            value={loading ? "—" : fmtDuration(overview.avg_engagement_time)} />
        </section>

        {/* ── TRAFFIC TRENDS ── */}
        <SectionDivider label="Traffic Trends" />

        <SectionCard title="Daily Trends" icon={TrendingUp} subtitle="Users · Sessions · Page Views over selected period">
          {dailyChartData.length > 0 ? (
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gU" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#06b6d4" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                  <XAxis dataKey="label" stroke="#475569" fontSize={10} tickLine={false} interval="preserveStartEnd" />
                  <YAxis stroke="#475569" fontSize={10} tickLine={false} axisLine={false} />
                  <Tooltip {...TT} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
                  <Line type="monotone" dataKey="Users"     stroke="#06b6d4" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Sessions"  stroke="#8b5cf6" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Pageviews" stroke="#f59e0b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyState loading={loading} label="trend" />}
        </SectionCard>

        {/* ── ACQUISITION ── */}
        <SectionDivider label="Acquisition" />

        <SectionCard title="Traffic Channels" icon={BarChart3} error={errors.trafficSources}
          subtitle="Sessions by default channel group">
          <div className="grid gap-5 xl:grid-cols-2">
            {channelBarData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={channelBarData} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                    <XAxis type="number" stroke="#475569" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="name" stroke="#475569" fontSize={10} tickLine={false} width={110} />
                    <Tooltip {...TT} />
                    <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
                    <Bar dataKey="Sessions" fill="#06b6d4" radius={[0,4,4,0]} />
                    <Bar dataKey="Users"    fill="#8b5cf6" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <EmptyState loading={loading} label="traffic channel" />}
            <ScrollTable
              headers={["Channel", "Source", "Sessions", "Users", "Eng%"]}
              emptyMsg={loading ? "Loading…" : "No data for this period."}
              rows={trafficSources.map(d => [
                d.channel || `${d.source}/${d.medium}`,
                <span className="text-slate-400 text-xs">{d.source}</span>,
                <span className="text-cyan-300">{fmt(d.sessions)}</span>,
                fmt(d.users),
                fmtPct(d.engagement_rate),
              ])}
            />
          </div>
        </SectionCard>

        {/* ── CONTENT ── */}
        <SectionDivider label="Content" />

        <div className="grid gap-5 xl:grid-cols-2">
          <SectionCard title="Top Pages" icon={Link} error={errors.topPages} subtitle="Most viewed pages">
            {topPagesBarData.length > 0 ? (
              <>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topPagesBarData} layout="vertical" margin={{ left: 8, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                      <XAxis type="number" stroke="#475569" fontSize={10} tickLine={false} axisLine={false} />
                      <YAxis type="category" dataKey="name" stroke="#475569" fontSize={10} tickLine={false} width={110} />
                      <Tooltip {...TT} />
                      <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
                      <Bar dataKey="Views" fill="#f59e0b" radius={[0,4,4,0]} />
                      <Bar dataKey="Users" fill="#10b981" radius={[0,4,4,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4">
                  <ScrollTable
                    headers={["Page", "Views", "Users", "Avg Time"]}
                    emptyMsg="No data"
                    rows={topPages.map(d => [
                      <span className="text-xs truncate block max-w-[180px]" title={d.page}>{d.page || "/"}</span>,
                      <span className="text-amber-300">{fmt(d.views)}</span>,
                      fmt(d.users),
                      fmtDuration(d.avg_time),
                    ])}
                  />
                </div>
              </>
            ) : <EmptyState loading={loading} label="page" />}
          </SectionCard>

          <SectionCard title="Landing Pages" icon={MousePointerClick} error={errors.landingPages}
            subtitle="Entry pages — where users first arrive">
            <ScrollTable
              headers={["Landing Page", "Sessions", "Users", "Bounce%"]}
              emptyMsg={loading ? "Loading…" : "No landing page data for this period."}
              rows={landingPages.map(d => [
                <span className="text-xs truncate block max-w-[180px]" title={d.page}>{d.page || "/"}</span>,
                <span className="text-cyan-300">{fmt(d.sessions)}</span>,
                fmt(d.users),
                fmtPct(d.bounce_rate),
              ])}
            />
          </SectionCard>
        </div>

        {/* ── EVENTS ── */}
        <SectionDivider label="Events" />

        <SectionCard title="Events" icon={Zap} error={errors.events}
          subtitle="All GA4 events fired in the selected period" badge="GA4">
          <div className="grid gap-5 xl:grid-cols-2">
            {eventsBarData.length > 0 ? (
              <HorizBar data={eventsBarData} dataKey="Count" height={280} />
            ) : <EmptyState loading={loading} label="event" />}
            <ScrollTable
              headers={["Event", "Count", "Users", "Per User"]}
              emptyMsg={loading ? "Loading…" : "No events for this period."}
              rows={events.map(d => [
                d.event || "-",
                <span className="text-cyan-300">{fmt(d.count)}</span>,
                fmt(d.users),
                String(d.count_per_user ?? "—"),
              ])}
            />
          </div>
        </SectionCard>

        {/* ── AUDIENCE ── */}
        <SectionDivider label="Audience" />

        {/* Countries */}
        <SectionCard title="Traffic by Country" icon={Globe} error={errors.countries}
          subtitle="Top 10 countries by active users">
          {countriesBarData.length > 0 ? (
            <div className="grid gap-5 xl:grid-cols-2">
              <HorizBar data={countriesBarData} dataKey="Users" height={260} />
              <ScrollTable
                headers={["Country", "Users", "Sessions"]}
                emptyMsg="No data"
                rows={countries.map(d => [
                  d.country || "-",
                  <span className="text-cyan-300">{fmt(d.users)}</span>,
                  fmt(d.sessions),
                ])}
              />
            </div>
          ) : <EmptyState loading={loading} label="country" />}
        </SectionCard>

        {/* Devices + Browsers + OS */}
        <div className="grid gap-5 lg:grid-cols-3">
          <SectionCard title="Devices" icon={Monitor} error={errors.devices} subtitle="By device category">
            {devicePieData.length > 0 ? (
              <>
                <DonutChart data={devicePieData} />
                <PieLegend data={devicePieData} />
              </>
            ) : <EmptyState loading={loading} label="device" />}
          </SectionCard>

          <SectionCard title="Browsers" icon={Globe} error={errors.browsers} subtitle="By browser">
            {browserPieData.length > 0 ? (
              <>
                <DonutChart data={browserPieData} />
                <PieLegend data={browserPieData} />
              </>
            ) : <EmptyState loading={loading} label="browser" />}
          </SectionCard>

          <SectionCard title="Operating Systems" icon={Laptop} error={errors.os} subtitle="By OS">
            {osPieData.length > 0 ? (
              <>
                <DonutChart data={osPieData} />
                <PieLegend data={osPieData} />
              </>
            ) : <EmptyState loading={loading} label="OS" />}
          </SectionCard>
        </div>

        {/* ── SEARCH CONSOLE ── */}
        <SectionDivider label="SEO · Search Console" />

        <SectionCard title="Keyword Performance" icon={Search} error={errors.scKeywords}
          subtitle="Google Search Console — clicks, impressions, CTR, position" badge="Search Console">
          <div className="grid gap-5 xl:grid-cols-2">
            {scKeywords.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={scKeywords.slice(0, 10).map(d => ({
                      name: (d.keys?.[0] || "-").slice(0, 22),
                      Clicks: Number(d.clicks || 0),
                      Impressions: Number(d.impressions || 0),
                    }))}
                    layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                    <XAxis type="number" stroke="#475569" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="name" stroke="#475569" fontSize={10} tickLine={false} width={130} />
                    <Tooltip {...TT} />
                    <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
                    <Bar dataKey="Clicks"      fill="#06b6d4" radius={[0,4,4,0]} />
                    <Bar dataKey="Impressions" fill="#6366f1" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <EmptyState loading={loading} label="keyword" />}
            <ScrollTable
              headers={["Keyword", "Clicks", "Impressions", "CTR", "Pos"]}
              emptyMsg={loading ? "Loading…" : "No keyword data for this period."}
              rows={scKeywords.map(d => [
                d.keys?.[0] || "-",
                <span className="text-cyan-300">{fmt(d.clicks)}</span>,
                fmt(d.impressions),
                fmtPct(Number(d.ctr || 0) * 100),
                Number(d.position || 0).toFixed(1),
              ])}
            />
          </div>
        </SectionCard>

      </div>
    </div>
  );
}
